import React, { useState, useEffect } from 'react';
import { X, Loader2, Car, Gauge, Cog, Fuel, Users, DoorOpen, Factory, AlertCircle, Download, FileText, MapPin, AlertTriangle, CheckCircle, Phone, Building2 } from 'lucide-react';
import { formatPhone } from '../utils/validation';
import { VINDecodeResult } from '../services/vinService';
import { generatePdfReport, NmvtisVehicleDataResponse, extractVehicleInfo, getStateContactInfo, StateContactInfo, getErrorMessage } from '../services/vehicleReportService';
import { getLogById, ApiRequestLogDetail } from '../services/searchHistoryService';
import { NvsEventData } from '../services/nvsService';

interface ReportViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  logId: string;
}

const ReportViewerModal: React.FC<ReportViewerModalProps> = ({
  isOpen,
  onClose,
  logId,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logData, setLogData] = useState<ApiRequestLogDetail | null>(null);
  const [decodeData, setDecodeData] = useState<VINDecodeResult | null>(null);
  const [nmvtisData, setNmvtisData] = useState<NmvtisVehicleDataResponse | null>(null);
  const [nvsData, setNvsData] = useState<NvsEventData | null>(null);
  const [stateContactInfo, setStateContactInfo] = useState<Record<string, StateContactInfo>>({});
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen || !logId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setLogData(null);
      setDecodeData(null);
      setNmvtisData(null);
      setNvsData(null);
      setStateContactInfo({});

      try {
        const data = await getLogById(logId);
        setLogData(data);

        // Parse the response body based on request type
        if (data.responseBody) {
          try {
            const parsedResponse = JSON.parse(data.responseBody);

            if (data.requestType === 'NMVTIS') {
              // NMVTIS response has a 'data' property with the vehicle data
              let nmvtisResponseData: NmvtisVehicleDataResponse;
              if (parsedResponse.data) {
                nmvtisResponseData = parsedResponse.data;
              } else {
                // In case the response is the data itself
                nmvtisResponseData = parsedResponse;
              }
              setNmvtisData(nmvtisResponseData);

              // Extract unique state codes from title history and fetch contact info
              const vehicleInfo = extractVehicleInfo(nmvtisResponseData);
              if (vehicleInfo?.titles && vehicleInfo.titles.length > 0) {
                const stateCodes = Array.from(new Set(vehicleInfo.titles.map(t => t.state).filter(Boolean))) as string[];
                if (stateCodes.length > 0) {
                  const contactInfo = await getStateContactInfo(stateCodes);
                  setStateContactInfo(contactInfo);
                }
              }
            } else if (data.requestType === 'NVS') {
              // NVS response has a 'data' property with event data
              const eventData: NvsEventData = parsedResponse.data || parsedResponse;
              setNvsData(eventData);
            } else {
              // VIN Decode response is the decode result directly
              setDecodeData(parsedResponse);
            }
          } catch (parseError) {
            console.error('Failed to parse response body:', parseError);
            setError(getErrorMessage(parseError));
          }
        } else {
          setError('No stored response data available for this report');
        }
      } catch (err: any) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, logId]);

  const handleDownloadPdf = async () => {
    if (!logData?.vin) return;

    setDownloading(true);
    try {
      await generatePdfReport({ vin: logData.vin });
    } catch (err: any) {
      console.error('Failed to download PDF:', err);
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  const reportType = logData?.requestType || 'Report';
  const vin = logData?.vin || '';

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">
              {reportType === 'NMVTIS' ? 'NMVTIS Vehicle Report'
                : reportType === 'NVS' ? 'Theft/Lien Report'
                : 'VIN Decode Report'}
            </h2>
            <p className="text-sm text-slate-400 font-mono">{vin}</p>
          </div>
          <div className="flex items-center gap-2">
            {reportType === 'NMVTIS' && nmvtisData && (
              <button
                onClick={handleDownloadPdf}
                disabled={downloading}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors border border-white/10 disabled:opacity-50"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download PDF
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <div className="text-center">
                <Loader2 className="w-10 h-10 text-[#AD0000] animate-spin mx-auto mb-4" />
                <p className="text-slate-400">Loading report...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <p className="text-red-400 font-medium mb-2">Failed to load report</p>
                <p className="text-slate-500 text-sm">{error}</p>
              </div>
            </div>
          ) : reportType === 'NMVTIS' && nmvtisData ? (
            <NMVTISContent data={nmvtisData} vin={vin} stateContactInfo={stateContactInfo} />
          ) : reportType === 'NVS' && nvsData ? (
            <NVSContent data={nvsData} vin={vin} />
          ) : reportType === 'VIN Decode' && decodeData ? (
            <VINDecodeContent data={decodeData} />
          ) : (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <p className="text-yellow-400 font-medium mb-2">No data available</p>
                <p className="text-slate-500 text-sm">The stored report data could not be displayed</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// NMVTIS Content Component
const NMVTISContent: React.FC<{
  data: NmvtisVehicleDataResponse;
  vin: string;
  stateContactInfo: Record<string, StateContactInfo>;
}> = ({ data, vin, stateContactInfo }) => {
  const vehicleInfo = extractVehicleInfo(data);

  if (!vehicleInfo) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <p className="text-yellow-400 font-medium">Unable to parse NMVTIS data</p>
        </div>
      </div>
    );
  }

  const hasExceptions = vehicleInfo.exceptions && vehicleInfo.exceptions.length > 0;
  const hasTitleHistory = vehicleInfo.titles && vehicleInfo.titles.length > 0;
  const hasStateContactInfo = Object.keys(stateContactInfo).length > 0;

  return (
    <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
      {/* VIN Header */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="w-14 h-14 rounded-xl bg-slate-800 flex items-center justify-center border border-slate-700">
          <FileText className="w-7 h-7 text-slate-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-white font-mono">{vin}</h3>
          <p className="text-slate-500 text-sm mt-1">NMVTIS Vehicle History Report</p>
        </div>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatusCard
          icon={vehicleInfo.hasTheftRecord ? AlertTriangle : CheckCircle}
          label="Theft Record"
          status={vehicleInfo.hasTheftRecord ? 'Found' : 'None'}
          isAlert={vehicleInfo.hasTheftRecord}
        />
        <StatusCard
          icon={vehicleInfo.hasSalvageRecord ? AlertTriangle : CheckCircle}
          label="Salvage Record"
          status={vehicleInfo.hasSalvageRecord ? 'Found' : 'None'}
          isAlert={vehicleInfo.hasSalvageRecord}
        />
        <StatusCard
          icon={vehicleInfo.hasFloodRecord ? AlertTriangle : CheckCircle}
          label="Flood Record"
          status={vehicleInfo.hasFloodRecord ? 'Found' : 'None'}
          isAlert={vehicleInfo.hasFloodRecord}
        />
        <StatusCard
          icon={vehicleInfo.hasJunkRecord ? AlertTriangle : CheckCircle}
          label="Junk/Dismantled"
          status={vehicleInfo.hasJunkRecord ? 'Found' : 'None'}
          isAlert={vehicleInfo.hasJunkRecord}
        />
      </div>

      {/* Title History */}
      {hasTitleHistory && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">Title History</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">State</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Odometer</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Type</th>
                </tr>
              </thead>
              <tbody>
                {vehicleInfo.titles.map((title, index) => (
                  <tr key={index} className="border-b border-slate-700/50 last:border-0">
                    <td className="py-3 px-3 text-white text-sm">{title.state}</td>
                    <td className="py-3 px-3 text-slate-400 text-sm">{title.date || '-'}</td>
                    <td className="py-3 px-3 text-slate-400 text-sm">
                      {title.odometer ? `${title.odometer.toLocaleString()} ${title.odometerUnit === 'K' ? 'km' : 'mi'}` : 'EXEMPT'}
                    </td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        title.isHistoric
                          ? 'bg-slate-600/50 text-slate-300'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {title.isHistoric ? 'Historic' : 'Current'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* State Contact Information */}
      {hasStateContactInfo && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">State Contact Information</h4>
          </div>
          <div className="space-y-4">
            {Object.values(stateContactInfo).map((info, index) => (
              <div key={index} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
                <div className="flex items-start justify-between mb-3">
                  <h5 className="text-white font-semibold">{info.stateName}</h5>
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
                    {info.stateCode}
                  </span>
                </div>
                <p className="text-slate-300 text-sm mb-3">{info.instructions}</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  {info.phone && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Phone className="w-4 h-4" />
                      <span>{formatPhone(info.phone)}</span>
                    </div>
                  )}
                  {info.mailingAddress && (
                    <div className="flex items-start gap-2 text-slate-400">
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                      <span className="whitespace-pre-line">{info.mailingAddress}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Exceptions/Notes */}
      {hasExceptions && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">Notes & Exceptions</h4>
          </div>
          <div className="space-y-2">
            {vehicleInfo.exceptions.map((exception, index) => (
              <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                <p className="text-slate-300 text-sm">{exception.text}</p>
                <p className="text-slate-600 text-xs mt-1">Code: {exception.code} | Severity: {exception.severity}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No records message */}
      {!hasExceptions && !hasTitleHistory && (
        <div className="bg-green-500/10 rounded-xl border border-green-500/30 p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-green-400 font-medium">No adverse records found</p>
          <p className="text-slate-500 text-sm mt-1">This vehicle has a clean NMVTIS history</p>
        </div>
      )}
    </div>
  );
};

// Status Card Component for NMVTIS
const StatusCard: React.FC<{
  icon: React.FC<{ className?: string }>;
  label: string;
  status: string;
  isAlert: boolean;
}> = ({ icon: Icon, label, status, isAlert }) => (
  <div className={`rounded-lg border p-3 text-center ${
    isAlert
      ? 'bg-red-500/10 border-red-500/30'
      : 'bg-green-500/10 border-green-500/30'
  }`}>
    <Icon className={`w-6 h-6 mx-auto mb-2 ${isAlert ? 'text-red-400' : 'text-green-400'}`} />
    <p className={`font-semibold text-sm ${isAlert ? 'text-red-400' : 'text-green-400'}`}>{status}</p>
    <p className="text-slate-500 text-xs">{label}</p>
  </div>
);

// NVS (Theft/Lien) Content Component
const NVSContent: React.FC<{ data: NvsEventData; vin: string }> = ({ data, vin }) => {
  const hasLiens = data.liens && data.liens.length > 0;
  const hasStolen = data.stolenRecords && data.stolenRecords.length > 0;
  const hasImpounds = data.impounds && data.impounds.length > 0;
  const hasExports = data.exports && data.exports.length > 0;
  const hasRecalls = data.recalls && data.recalls.length > 0;
  const hasEbay = data.ebayAuctions && data.ebayAuctions.length > 0;
  const hasCustoms = data.customsInquiries && data.customsInquiries.length > 0;
  const hasErrors = data.errors && data.errors.length > 0;
  const hasAnyData = hasLiens || hasStolen || hasImpounds || hasExports || hasRecalls || hasEbay || hasCustoms;

  return (
    <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
      {/* VIN Header */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="w-14 h-14 rounded-xl bg-slate-800 flex items-center justify-center border border-slate-700">
          <FileText className="w-7 h-7 text-slate-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-white font-mono">{vin}</h3>
          <p className="text-slate-500 text-sm mt-1">Theft/Lien Report</p>
        </div>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatusCard
          icon={hasStolen ? AlertTriangle : CheckCircle}
          label="Stolen"
          status={hasStolen ? 'Found' : 'None'}
          isAlert={hasStolen}
        />
        <StatusCard
          icon={hasLiens ? AlertTriangle : CheckCircle}
          label="Liens"
          status={hasLiens ? `${data.liens.length} Found` : 'None'}
          isAlert={hasLiens}
        />
        <StatusCard
          icon={hasImpounds ? AlertTriangle : CheckCircle}
          label="Impounds"
          status={hasImpounds ? `${data.impounds.length} Found` : 'None'}
          isAlert={hasImpounds}
        />
        <StatusCard
          icon={hasExports ? AlertTriangle : CheckCircle}
          label="Exports"
          status={hasExports ? `${data.exports.length} Found` : 'None'}
          isAlert={hasExports}
        />
      </div>

      {/* Stolen Records */}
      {hasStolen && (
        <div className="bg-red-500/10 rounded-xl border border-red-500/30 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h4 className="text-red-400 font-semibold">Stolen Records</h4>
          </div>
          <div className="space-y-2">
            {data.stolenRecords.map((record, index) => (
              <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-red-500/20">
                <div className="space-y-1">
                  {record.status && <p className="text-white text-sm"><span className="text-slate-400">Status:</span> {record.status}</p>}
                  {record.state && <p className="text-white text-sm"><span className="text-slate-400">State:</span> {record.state}</p>}
                  {record.lastEventDate && <p className="text-white text-sm"><span className="text-slate-400">Date:</span> {record.lastEventDate}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Liens */}
      {hasLiens && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">Lien Records</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Lienholder</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.liens.map((lien, index) => (
                  <tr key={index} className="border-b border-slate-700/50 last:border-0">
                    <td className="py-3 px-3 text-white text-sm">{lien.lienholder || '-'}</td>
                    <td className="py-3 px-3 text-slate-400 text-sm">{lien.lienDate || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Impounds */}
      {hasImpounds && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">Impound Records</h4>
          </div>
          <div className="space-y-2">
            {data.impounds.map((impound, index) => (
              <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                <div className="space-y-1">
                  {impound.state && <p className="text-white text-sm"><span className="text-slate-400">State:</span> {impound.state}</p>}
                  {impound.impoundDate && <p className="text-white text-sm"><span className="text-slate-400">Date:</span> {impound.impoundDate}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Exports */}
      {hasExports && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">Export Records</h4>
          </div>
          <div className="space-y-2">
            {data.exports.map((exp, index) => (
              <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                <div className="space-y-1">
                  {exp.state && <p className="text-white text-sm"><span className="text-slate-400">State:</span> {exp.state}</p>}
                  {exp.exportDate && <p className="text-white text-sm"><span className="text-slate-400">Date:</span> {exp.exportDate}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recalls */}
      {hasRecalls && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">Recall Records</h4>
          </div>
          <div className="space-y-2">
            {data.recalls.map((recall, index) => (
              <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                <p className="text-white text-sm">{recall.reason || 'No details available'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* eBay Auctions */}
      {hasEbay && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Car className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">eBay Auction Records</h4>
          </div>
          <div className="space-y-2">
            {data.ebayAuctions.map((auction, index) => (
              <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                <div className="space-y-1">
                  {auction.itemNumber && <p className="text-white text-sm"><span className="text-slate-400">Item #:</span> {auction.itemNumber}</p>}
                  {auction.auctionDate && <p className="text-white text-sm"><span className="text-slate-400">Date:</span> {auction.auctionDate}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Customs Inquiries */}
      {hasCustoms && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">Customs Inquiries</h4>
          </div>
          <div className="space-y-2">
            {data.customsInquiries.map((inquiry, index) => (
              <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                {inquiry.searchDate && <p className="text-white text-sm"><span className="text-slate-400">Date:</span> {inquiry.searchDate}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Errors */}
      {hasErrors && (
        <div className="bg-yellow-500/10 rounded-xl border border-yellow-500/30 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-yellow-400" />
            <h4 className="text-yellow-400 font-semibold">Notes</h4>
          </div>
          <div className="space-y-2">
            {data.errors.map((err, index) => (
              <div key={index} className="p-3 bg-slate-900/50 rounded-lg border border-yellow-500/20">
                <p className="text-slate-300 text-sm">{err.message || `Error code: ${err.code}`}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clean report */}
      {!hasAnyData && !hasErrors && (
        <div className="bg-green-500/10 rounded-xl border border-green-500/30 p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-green-400 font-medium">No adverse records found</p>
          <p className="text-slate-500 text-sm mt-1">This vehicle has no theft, lien, impound, or export records</p>
        </div>
      )}
    </div>
  );
};

// VIN Decode Content Component
const VINDecodeContent: React.FC<{ data: VINDecodeResult }> = ({ data }) => {
  return (
    <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
      {/* Vehicle Banner */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="w-14 h-14 rounded-xl bg-slate-800 flex items-center justify-center border border-slate-700">
          <Car className="w-7 h-7 text-slate-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-white">
            {data.modelYear || data.year} {data.make} {data.model}
          </h3>
          {data.trim && (
            <span className="inline-block mt-1 px-2.5 py-0.5 bg-[#AD0000]/20 text-[#AD0000] text-xs font-medium rounded-full border border-[#AD0000]/30">
              {data.trim}
            </span>
          )}
          <p className="text-slate-500 text-sm mt-1">{data.manufacturerName || data.manufacturer}</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard icon={Gauge} label="Engine" value={data.engine?.displacementL ? `${data.engine.displacementL}L` : '-'} subvalue={data.engine?.configuration} />
        <StatCard icon={Cog} label="Transmission" value={data.transmission?.speeds ? `${data.transmission.speeds}-Spd` : '-'} subvalue={data.transmission?.style} />
        <StatCard icon={Fuel} label="Fuel" value={data.engine?.fuelType || '-'} />
        <StatCard icon={Users} label="Seating" value={data.seats ? `${data.seats} Pass` : '-'} subvalue={data.seatRows ? `${data.seatRows} Rows` : undefined} />
        <StatCard icon={DoorOpen} label="Doors" value={data.doors || '-'} />
        <StatCard icon={Car} label="Body" value={data.bodyClass || '-'} subvalue={data.vehicleType} />
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Engine Details */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Gauge className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">Engine Specifications</h4>
          </div>
          <div className="space-y-3">
            <DetailRow label="Model" value={data.engine?.model} />
            <DetailRow label="Configuration" value={data.engine?.configuration} />
            <DetailRow label="Displacement" value={data.engine?.displacementL ? `${data.engine.displacementL}L (${data.engine.displacementCC} CC)` : undefined} />
            <DetailRow label="Cylinders" value={data.engine?.cylinders} />
            <DetailRow label="Horsepower" value={data.engine?.horsepower} />
          </div>
        </div>

        {/* Drivetrain */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Cog className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">Drivetrain</h4>
          </div>
          <div className="space-y-3">
            <DetailRow label="Transmission" value={data.transmission?.style} />
            <DetailRow label="Speeds" value={data.transmission?.speeds} />
            <DetailRow label="Drive Type" value={data.driveType} />
            <DetailRow label="Brake System" value={data.brakeSystem} />
            <DetailRow label="GVWR" value={data.gvwr} />
          </div>
        </div>

        {/* Manufacturing */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Factory className="w-5 h-5 text-slate-400" />
            <h4 className="text-white font-semibold">Manufacturing</h4>
          </div>
          <div className="space-y-3">
            <DetailRow label="Manufacturer" value={data.manufacturerName || data.manufacturer} />
            <DetailRow label="Plant City" value={data.plant?.city} />
            <DetailRow label="State" value={data.plant?.state} />
            <DetailRow label="Country" value={data.plant?.country || data.country} />
            <DetailRow label="Assembly Plant" value={data.assemblyPlant} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const StatCard: React.FC<{
  icon: React.FC<{ className?: string }>;
  label: string;
  value: string;
  subvalue?: string;
}> = ({ icon: Icon, label, value, subvalue }) => (
  <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-3 text-center">
    <Icon className="w-5 h-5 text-slate-500 mx-auto mb-2" />
    <p className="text-white font-semibold text-sm">{value}</p>
    <p className="text-slate-500 text-xs">{label}</p>
    {subvalue && <p className="text-slate-600 text-xs">{subvalue}</p>}
  </div>
);

const DetailRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-slate-700/50 last:border-0">
    <span className="text-slate-400 text-sm">{label}</span>
    <span className="text-white font-medium text-sm text-right max-w-[60%]">{value || '-'}</span>
  </div>
);

export default ReportViewerModal;
